import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { PropertyResponseType } from '@services';
import { collectOwnershipRows } from '@/shared/utils/property-detail-fields';
import { collectOwnershipAttributeRows } from '@/shared/utils/property-attribute-rows';
import { buildWhatsAppLink } from '@/shared/utils/whatsapp-link';
import { formatPhoneDisplay } from '@/shared/utils/format-phone';
import { Anchor, Button, Text } from '@/shared/ui';
import { CopyIcon } from '@/shared/ui/radix-icons';
import { SectionCard, InfoRow, EditCardButton, EmptyCardHint } from '../SectionCard';
import { usePropertyOwnerContact } from '../hooks/usePropertyOwnerContact';

interface OwnershipCardProps {
  property: PropertyResponseType;
  agencyId: string;
  /** Opens the property editor on the ownership section. */
  onEdit?: () => void;
}

/**
 * One of the two owner-contact actions, disabled when there is nothing to
 * open.
 *
 * Rendered as a plain span rather than a dead link when no contact is on
 * file: a link that dials nowhere is worse than a button that says it cannot.
 */
const ContactAction: React.FC<{ href?: string; label: string; testId: string }> = ({
  href,
  label,
  testId,
}) => {
  const shared =
    'flex h-[34px] min-w-0 flex-1 items-center justify-center rounded-(--radius-md) border border-solid border-[var(--border-medium)] px-[var(--space-3)] text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] whitespace-nowrap';

  if (!href) {
    return (
      <Text
        component="span"
        data-testid={testId}
        aria-disabled="true"
        className={`${shared} bg-[var(--surface-tertiary)] text-[var(--text-tertiary)]`}
      >
        {label}
      </Text>
    );
  }

  return (
    <Anchor
      href={href}
      data-testid={testId}
      target="_blank"
      rel="noopener noreferrer"
      className={`${shared} bg-[var(--surface-primary)] text-[var(--text-primary)] no-underline hover:bg-[var(--surface-hover)]`}
    >
      {label}
    </Anchor>
  );
};

/**
 * The owner's phone, written out so it can be read aloud or passed on, with a
 * button that copies it.
 */
const OwnerPhoneRow: React.FC<{ phone: string }> = ({ phone }) => {
  const { t } = useTranslation('features/properties');
  const formatted = formatPhoneDisplay(phone);

  const handleCopy = () => {
    navigator.clipboard
      .writeText(formatted)
      .then(() => toast.success(t('detailView.ownership.phoneCopied')))
      .catch(() => toast.error(t('detailView.ownership.copyFailed')));
  };

  return (
    <div className="flex items-center justify-between gap-[var(--space-3)] py-[var(--space-2)]">
      <Text
        as="span"
        className="shrink-0 text-[length:var(--text-body-sm)] leading-[var(--leading-snug)] text-[var(--text-tertiary)]"
      >
        {t('detailView.ownership.phone')}
      </Text>
      <Text component="span" className="flex min-w-0 items-center gap-[var(--space-2)]">
        <Text
          as="span"
          data-testid="property-owner-phone"
          className="min-w-0 break-words text-right text-[length:var(--text-body-sm)] [font-weight:var(--font-bold)] leading-[var(--leading-snug)] text-[var(--text-primary)] select-all"
        >
          {formatted}
        </Text>
        <Button
          variant="ghost-text"
          size="custom"
          onClick={handleCopy}
          data-testid="property-owner-phone-copy"
          aria-label={t('detailView.ownership.copyPhone')}
          title={t('detailView.ownership.copyPhone')}
          className="size-[28px] shrink-0 p-0"
        >
          <CopyIcon aria-hidden />
        </Button>
      </Text>
    </div>
  );
};

/**
 * Sidebar card listing ownership and mandate attributes saved on the property.
 *
 * Resolves owner_client_id to the client display name when available, shows
 * the owner's phone written out, and offers the two ways a broker reaches an owner from here — a call and a
 * message — drawn from the owner's client record rather than the property.
 *
 * @param props.property - The loaded property.
 * @param props.agencyId - Agency owning the property and its clients.
 * Always rendered, empty or not, so the owner can be filled in from its pencil.
 *
 * @param props.onEdit - Opens the editor on the ownership section.
 * @returns The rendered card.
 */
export const OwnershipCard: React.FC<OwnershipCardProps> = ({ property, agencyId, onEdit }) => {
  const { t } = useTranslation('features/properties');
  const rows = useMemo(() => collectOwnershipRows(property.ownership), [property.ownership]);
  // Exclusivity here and the mandate's exclusive type above coexist (plan
  // §12): both are shown as stored, neither is derived from the other.
  const attributeRows = useMemo(
    () => collectOwnershipAttributeRows(property.ownership, t),
    [property.ownership, t],
  );
  const owner = usePropertyOwnerContact(agencyId, property.ownership?.owner_client_id);

  const messageHref = owner.message
    ? owner.message.includes('@')
      ? `mailto:${owner.message}`
      : buildWhatsAppLink(owner.message)
    : undefined;

  return (
    <SectionCard
      title={t('detailView.ownership.title')}
      data-testid="property-ownership-card"
      titleAccessory={onEdit ? <EditCardButton onClick={onEdit} data-testid="property-ownership-edit" /> : undefined}
    >
      {rows.length + attributeRows.length === 0 ? <EmptyCardHint /> : null}
      {rows.map((row) => {
        if (row.labelKey === 'detailView.ownership.owner') {
          return (
            <React.Fragment key={row.labelKey}>
              <InfoRow
                label={t(row.labelKey)}
                value={owner.name ?? (owner.loading ? '…' : row.value)}
              />
              {owner.phoneDisplay ? <OwnerPhoneRow phone={owner.phoneDisplay} /> : null}
            </React.Fragment>
          );
        }
        const value =
          row.labelKey === 'detailView.ownership.mandateType'
            ? t(`form.mandate.${row.value}`, { defaultValue: row.value })
            : row.value;
        return <InfoRow key={row.labelKey} label={t(row.labelKey)} value={value} />;
      })}
      {attributeRows.map((row) => (
        <InfoRow key={row.labelKey} label={t(row.labelKey)} value={row.value} />
      ))}

      {property.ownership?.owner_client_id ? (
      <div className="mt-[var(--space-3)] flex gap-[var(--space-2)]">
        <ContactAction
          href={owner.phone ? `tel:${owner.phone}` : undefined}
          label={t('detailView.ownership.call')}
          testId="property-owner-call"
        />
        <ContactAction
          href={messageHref}
          label={t('detailView.ownership.message')}
          testId="property-owner-message"
        />
      </div>
      ) : null}
    </SectionCard>
  );
};
